/**
 * R26 — the application's top-level views. Classic was retired here, so every
 * view is a `unified-*` destination (legacy `?view` values remap to these in
 * App.parseView). This type lived in the now-deleted NavSidebar; it lives here,
 * with the destination registry, as the single source of truth.
 */
export type NavView =
  | 'unified-today'
  | 'unified'
  | 'unified-followups'
  | 'unified-engagement'
  | 'unified-connection-requests'
  | 'unified-connect'
  | 'unified-job-changes'
  | 'unified-keep-in-touch'
  | 'unified-contacts'
  | 'unified-candidates'
  | 'unified-automation'
  | 'unified-settings';

/**
 * R11 — the canonical destination registry: ONE name per destination, in ONE
 * place. The nav rail labels, the document.title, and (R12 g-keys / R13 ⌘K) all
 * read from here, so a destination can't be called "Inbox" in the rail and
 * "Unified inbox" in the tab title. `id` is the canonical `?view` slug.
 */
export type DestGroup = 'triage' | 'outreach' | 'people' | 'system';

export interface Destination {
  /** Canonical slug — stable across the redesign (the eventual routed ?view id). */
  id: string;
  /** The ONE display name, everywhere. */
  label: string;
  group: DestGroup;
  /** For view-destinations: the NavView it routes to. Hubs (outreach) open a modal. */
  view?: NavView;
}

export const DESTINATIONS = {
  today: { id: 'today', label: 'Today', group: 'triage', view: 'unified-today' },
  inbox: { id: 'inbox', label: 'Inbox', group: 'triage', view: 'unified' },
  followups: { id: 'followups', label: 'Follow-ups', group: 'triage', view: 'unified-followups' },
  engagement: {
    id: 'engagement',
    label: 'LinkedIn engagement',
    group: 'outreach',
    view: 'unified-engagement',
  },
  jobChanges: {
    id: 'job-changes',
    label: 'Job changes',
    group: 'outreach',
    view: 'unified-job-changes',
  },
  connectionRequests: {
    id: 'connection-requests',
    label: 'Connection requests',
    group: 'outreach',
    view: 'unified-connection-requests',
  },
  keepInTouch: {
    id: 'keep-in-touch',
    label: 'Keep in touch',
    group: 'outreach',
    view: 'unified-keep-in-touch',
  },
  connect: {
    id: 'connect',
    label: 'People you met',
    group: 'outreach',
    view: 'unified-connect',
  },
  contacts: { id: 'contacts', label: 'Contacts', group: 'people', view: 'unified-contacts' },
  identityMatches: {
    id: 'identity-matches',
    // batch-341 — retitled: the view is now the tabbed Contact-hygiene hub
    // (Duplicates / Junk / Attribution). The slug + view id stay 'unified-candidates'
    // so legacy links + ⌘K / g-keys keep working.
    label: 'Contact hygiene',
    group: 'people',
    view: 'unified-candidates',
  },
  automation: {
    id: 'automation',
    label: 'Automation log',
    group: 'system',
    view: 'unified-automation',
  },
  setup: { id: 'setup', label: 'Setup', group: 'system', view: 'unified-settings' },
} satisfies Record<string, Destination>;

export type DestinationKey = keyof typeof DESTINATIONS;

/** Canonical document.title per view — reads the ONE registry label. */
const VIEW_TITLE: Record<NavView, string> = {
  'unified-today': DESTINATIONS.today.label,
  unified: DESTINATIONS.inbox.label,
  'unified-followups': DESTINATIONS.followups.label,
  'unified-engagement': DESTINATIONS.engagement.label,
  'unified-connection-requests': DESTINATIONS.connectionRequests.label,
  'unified-job-changes': DESTINATIONS.jobChanges.label,
  'unified-keep-in-touch': DESTINATIONS.keepInTouch.label,
  'unified-connect': DESTINATIONS.connect.label,
  'unified-contacts': DESTINATIONS.contacts.label,
  'unified-candidates': DESTINATIONS.identityMatches.label,
  'unified-automation': DESTINATIONS.automation.label,
  'unified-settings': DESTINATIONS.setup.label,
};

export function viewTitle(view: NavView): string {
  return VIEW_TITLE[view];
}
